// Demo-grade seeded registry for the Workflow Library & Drilldown.
// Pure frontend — no backend wiring. Four workflows: 3 Fulfillment, 1 Corporate Actions.

import { Workflow, WorkflowRun, NodeStatus } from '../types/workflow';

// ---- Run generator (deterministic so the table doesn't flicker) ----
function seedRuns(workflowId: string, prefix: string, count: number, baseSuccess = 0.92): WorkflowRun[] {
  const runs: WorkflowRun[] = [];
  const now = Date.now();
  for (let i = 0; i < count; i++) {
    const startedMs = now - i * 17 * 60 * 1000 - (i % 3) * 90 * 1000;
    const duration = 4500 + ((i * 1373) % 8200);
    const completedMs = startedMs + duration;
    const failed = (i * 9301 + 1) % 100 > baseSuccess * 100;
    const status: 'succeeded' | 'failed' = failed ? 'failed' : 'succeeded';
    const retry = failed ? 1 + (i % 2) : 0;
    runs.push({
      run_id: `${prefix}-${(10000 + count - i).toString()}`,
      trace_id: `tr_${workflowId.slice(0, 4)}_${(100000 + i * 31).toString(16)}`,
      correlation_id: `cor_${(i * 7 + 13).toString(16)}`,
      started_at: new Date(startedMs).toISOString(),
      completed_at: new Date(completedMs).toISOString(),
      duration_ms: duration,
      status,
      trigger_source: i % 4 === 0 ? 'scheduled' : i % 4 === 1 ? 'event' : 'manual',
      initiated_by: i % 2 === 0 ? 'system' : 'op_user@northpoint.com',
      retry_count: retry,
      failed_node: failed ? 'job_match' : undefined,
      node_timeline: [
        { node_id: 'trigger', node_label: 'Trigger', started_at: new Date(startedMs).toISOString(), duration_ms: 80, status: 'success' as NodeStatus },
        { node_id: 'intake', node_label: 'Intake', started_at: new Date(startedMs + 80).toISOString(), duration_ms: 920, status: 'success' as NodeStatus },
        { node_id: 'ai_extract', node_label: 'AI Extract', started_at: new Date(startedMs + 1000).toISOString(), duration_ms: 2400, status: 'success' as NodeStatus },
        { node_id: 'job_match', node_label: 'Job Match', started_at: new Date(startedMs + 3400).toISOString(), duration_ms: 1200, status: failed ? 'failed' as NodeStatus : 'success' as NodeStatus },
        ...(failed ? [] : [
          { node_id: 'persist', node_label: 'Persist', started_at: new Date(startedMs + 4600).toISOString(), duration_ms: duration - 4600, status: 'success' as NodeStatus },
        ]),
      ],
      logs: [
        { ts: new Date(startedMs).toISOString(), level: 'info', message: `Run ${prefix}-${count - i} started` },
        { ts: new Date(startedMs + 1000).toISOString(), level: 'info', message: 'AI extraction begin' },
        { ts: new Date(startedMs + 3300).toISOString(), level: 'info', message: 'AI extraction complete (confidence=0.94)' },
        ...(failed
          ? [{ ts: new Date(startedMs + 4400).toISOString(), level: 'error' as const, message: 'Job match: no candidate above threshold' }]
          : [{ ts: new Date(completedMs).toISOString(), level: 'info' as const, message: 'Run succeeded' }]),
      ],
      payloads: {
        input: { source: i % 4 === 0 ? 'scan_upload' : 'event_bus', size_bytes: 24800 + i * 7 },
        output: { matched: !failed, confidence: failed ? 0.41 : 0.92 },
      },
    });
  }
  return runs;
}

// Common Claude system-prompt excerpt — sourced from domain/ai/agents/manager_agent.py
const ACE_PROMPT_EXCERPT = `You are Ace, BetaNXT's operational intelligence agent.

You have access to live ops data including jobs in the system, exception queues
(No Job Found, Received Short, On Hold), and recent intake log entries. When the
user asks operational questions, ground your response in this data rather than
speculating.`;

// =========================================================================
// 1) Material Receiving & Job Matching
// =========================================================================
const wfReceivingMatch: Workflow = {
  id: 'wf-receiving-match',
  name: 'Material Receiving & Job Matching',
  description: 'Scans and uploads of physical material are matched to active job records by Ace.',
  category: 'Fulfillment',
  owner: 'Fulfillment Ops',
  environment: 'prod',
  status: 'healthy',
  criticality: 'tier-1',
  version: '3.2.1',
  executions_24h: 412,
  success_rate: 96.4,
  avg_duration_ms: 7300,
  integrations_count: 4,
  last_run_at: '2026-05-27T14:42:11Z',
  last_deployed_at: '2026-05-18T19:03:22Z',
  ai_enabled: true,
  tags: ['fulfillment', 'vision', 'matching', 'mediant'],
  sla_status: 'on-track',
  active_runs: 3,

  business_summary:
    'When a physical material arrives (camera scan or file upload), this workflow extracts issuer, event date, and document type from the page using a vision model and matches it against active job orders. Successful matches are written to the matched-job-materials table; misses are routed to the exception queues.',
  kpis: {
    executions_per_day: 412,
    failure_rate: 3.6,
    avg_duration_ms: 7300,
    p95_duration_ms: 12400,
    retry_rate: 1.8,
    queue_depth: 6,
    human_interventions: 11,
    ai_confidence_avg: 0.91,
    token_usage_24h: 184_300,
    est_cost_per_run_usd: 0.018,
  },
  dependency_summary:
    'Upstream: warehouse intake (camera / upload). Downstream: matched_job_materials persistence, No Job Found / Received Short queues.',
  recent_incidents: [
    {
      id: 'INC-2138',
      title: 'Mediant lookup latency spike',
      severity: 'medium',
      status: 'resolved',
      opened_at: '2026-05-22T11:14:00Z',
      summary: 'p95 latency on Mediant client lookup rose to 4.2s for ~18 minutes. Auto-recovered.',
    },
  ],

  identity: {
    workflow_id: 'wf-receiving-match',
    namespace: 'fulfillment',
    slug: 'material-receiving-match',
    created_by: 'derrick@nexusfrontier.tech',
    created_at: '2025-11-04T16:00:00Z',
    updated_at: '2026-05-18T19:03:22Z',
    git_commit: '3dce121',
    deployment_environment: 'prod',
    lifecycle_state: 'active',
  },
  governance: {
    owner_team: 'Fulfillment Ops',
    escalation_contacts: ['ops-oncall@northpoint.com', 'fulfillment-lead@northpoint.com'],
    business_approver: 'VP Operations',
    technical_approver: 'Director, Platform Eng',
    compliance_scope: ['SOC 2', 'Internal Ops SLA'],
    data_classification: 'Internal',
    risk_level: 'medium',
  },
  policy: {
    retention_policy: 'Run records retained 13 months; scan images retained 90 days.',
    residency_policy: 'US-East only.',
    secrets_used: ['MEDIANT_API_KEY', 'ANTHROPIC_API_KEY', 'S3_UPLOAD_KEY'],
    approval_requirements: 'Single technical approver for non-prompt changes; dual for prompt/model.',
    data_access_scope: 'Scoped to fulfillment namespace tables.',
  },

  connections: [
    {
      id: 'c-mediant',
      name: 'Mediant DB',
      category: 'database',
      type: 'PostgreSQL (internal mirror)',
      authentication_type: 'mTLS',
      owner: 'Platform Eng',
      permissions_scope: 'read: jobs, clients, distributors',
      environment: 'prod',
      latency_ms: 142,
      uptime_pct: 99.97,
      last_success: '2026-05-27T14:42:08Z',
      rate_limits: '500 rps',
      schema_reference: 'mediant.public.jobs',
      secrets_reference: 'MEDIANT_API_KEY',
    },
    {
      id: 'c-jobs',
      name: 'Ace Jobs DB',
      category: 'database',
      type: 'PostgreSQL',
      authentication_type: 'IAM',
      owner: 'Platform Eng',
      permissions_scope: 'read/write: job_orders, matched_job_materials',
      environment: 'prod',
      latency_ms: 11,
      uptime_pct: 99.99,
      last_success: '2026-05-27T14:42:11Z',
      rate_limits: '2k rps',
    },
    {
      id: 'c-camera',
      name: 'Warehouse Camera Intake',
      category: 'intake',
      type: 'Camera capture',
      authentication_type: 'Device cert',
      owner: 'Fulfillment Ops',
      permissions_scope: 'write: intake_log',
      environment: 'prod',
      latency_ms: 90,
      uptime_pct: 99.92,
      last_success: '2026-05-27T14:41:55Z',
      rate_limits: 'n/a',
    },
    {
      id: 'c-anthropic',
      name: 'Anthropic Claude API',
      category: 'ai-provider',
      type: 'Vision + chat',
      authentication_type: 'API key',
      owner: 'Platform Eng',
      permissions_scope: 'claude-sonnet-4-6 (vision)',
      environment: 'prod',
      latency_ms: 1840,
      uptime_pct: 99.91,
      last_success: '2026-05-27T14:42:09Z',
      rate_limits: '50 rpm tier',
      secrets_reference: 'ANTHROPIC_API_KEY',
    },
  ],

  graph: {
    nodes: [
      { id: 'trigger', type: 'trigger', label: 'Intake Trigger', description: 'Fires on new camera capture or upload.', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 30, p95_latency_ms: 80, success_rate: 99.99, retries_24h: 0 }, io_schema: { input: '{ source, payload_ref }', output: '{ intake_id }' }, recent_logs: ['trigger fired source=camera'] },
      { id: 'intake', type: 'system', label: 'Validate Intake', description: 'Stores raw asset and writes intake_log row.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 220, p95_latency_ms: 410, success_rate: 99.95, retries_24h: 1 }, io_schema: { input: '{ intake_id }', output: '{ asset_path }' }, recent_logs: ['intake_log row written'] },
      { id: 'ai_extract', type: 'ai', label: 'AI Extract (Claude Vision)', description: 'Extracts issuer, event date, doc type from the scan.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 2100, p95_latency_ms: 3800, success_rate: 98.4, retries_24h: 6 }, io_schema: { input: '{ asset_path }', output: '{ issuer, event_date, doc_type, confidence }' }, recent_logs: ['claude-sonnet-4-6 extract confidence=0.94'] },
      { id: 'mediant_lookup', type: 'database', label: 'Mediant Lookup', description: 'Pulls candidate jobs from the Mediant mirror.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 140, p95_latency_ms: 380, success_rate: 99.6, retries_24h: 2 }, io_schema: { input: '{ issuer, event_date }', output: '{ candidates[] }' }, recent_logs: ['12 candidates returned'] },
      { id: 'job_match', type: 'ai', label: 'Score & Match', description: 'Scores candidates and selects best match with confidence.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 900, p95_latency_ms: 1600, success_rate: 96.4, retries_24h: 8 }, io_schema: { input: '{ candidates[], extraction }', output: '{ match_id, confidence }' }, recent_logs: ['match selected confidence=0.92'] },
      { id: 'confidence_gate', type: 'conditional', label: 'Confidence Gate', description: 'Routes by match confidence threshold (0.80).', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 5, p95_latency_ms: 10, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ confidence }', output: '{ branch }' }, recent_logs: ['branch=match'] },
      { id: 'persist', type: 'database', label: 'Persist Match', description: 'Writes matched_job_materials row.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 80, p95_latency_ms: 180, success_rate: 99.99, retries_24h: 0 }, io_schema: { input: '{ match_id }', output: '{ row_id }' }, recent_logs: ['row inserted'] },
      { id: 'route_no_job', type: 'queue', label: 'No Job Found', description: 'Routes low-confidence misses to the exception queue.', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 60, p95_latency_ms: 110, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ extraction }', output: '{ queue_item_id }' }, recent_logs: ['queued for triage'] },
      { id: 'notify', type: 'notification', label: 'Notify Op', description: 'Pings the operator in Ace on success.', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 40, p95_latency_ms: 70, success_rate: 99.9, retries_24h: 0 }, io_schema: { input: '{ row_id }', output: '{}' }, recent_logs: ['notification sent'] },
    ],
    edges: [
      { id: 'e1', source: 'trigger', target: 'intake' },
      { id: 'e2', source: 'intake', target: 'ai_extract' },
      { id: 'e3', source: 'ai_extract', target: 'mediant_lookup' },
      { id: 'e4', source: 'mediant_lookup', target: 'job_match' },
      { id: 'e5', source: 'job_match', target: 'confidence_gate' },
      { id: 'e6', source: 'confidence_gate', target: 'persist', label: '0.80 or higher' },
      { id: 'e7', source: 'confidence_gate', target: 'route_no_job', label: '< 0.80' },
      { id: 'e8', source: 'persist', target: 'notify' },
    ],
  },

  logic: {
    executive_summary:
      'Match physical material receipts to active jobs in under 10 seconds with > 96% accuracy. Misses route to human-reviewable exception queues.',
    trigger_conditions: [
      'New camera capture from warehouse station',
      'New file upload to /api/v1/jobs/upload-photo',
    ],
    sop_steps: [
      { title: '1. Validate intake', detail: 'Confirm asset is readable; write intake_log row with timestamp and source.' },
      { title: '2. Extract fields', detail: 'Claude vision extracts issuer name, event date, document type, confidence.' },
      { title: '3. Mediant lookup', detail: 'Query Mediant mirror for candidate jobs by issuer + event window.' },
      { title: '4. Score & match', detail: 'Score each candidate; pick top with confidence.' },
      { title: '5. Gate', detail: 'If confidence is 0.80 or higher, persist match. Otherwise route to No Job Found.' },
      { title: '6. Notify', detail: 'Surface result to the operator via Ace.' },
    ],
    decision_logic: [
      'Confidence 0.80 or higher → persist match',
      'Confidence from 0.50 up to 0.80 → human review (No Job Found queue)',
      'Confidence < 0.50 → No Job Found, marked low-signal',
    ],
    exception_handling: [
      'Vision timeout → retry up to 2 times with exponential backoff',
      'Mediant timeout → fall back to local cache for 60s',
      'Persist failure → re-queue with idempotency key',
    ],
    escalation_rules: [
      'No Job Found queue depth > 25 → page fulfillment-lead',
      'Vision API error rate > 5% over 10 min → page ops-oncall',
    ],
    ai_reasoning: {
      model: 'claude-sonnet-4-6',
      system_prompt_excerpt: ACE_PROMPT_EXCERPT,
      confidence_threshold: 0.8,
      grounding_sources: ['intake_log', 'mediant.public.jobs', 'ace_identity.md'],
      fallback_logic: 'If ANTHROPIC_API_KEY unset, manager_agent.py returns scripted mock response — workflow halts at AI Extract and routes raw to No Job Found.',
      safety_rules: [
        'No PII in logs (mask client name + account number)',
        'No outbound calls outside allowlisted Anthropic + Mediant endpoints',
      ],
      tool_permissions: ['vision.extract', 'db.read[mediant]', 'db.read[ops_jobs]'],
      evaluation_metrics: ['confidence_avg', 'precision_at_0.80', 'human_correction_rate'],
    },
  },

  observability: {
    timeline: [
      { ts: '2026-05-27T14:42:11Z', event: 'run succeeded', node_id: 'notify' },
      { ts: '2026-05-27T14:42:09Z', event: 'match persisted', node_id: 'persist' },
      { ts: '2026-05-27T14:42:07Z', event: 'AI extract complete (0.94)', node_id: 'ai_extract' },
      { ts: '2026-05-27T14:42:05Z', event: 'intake validated', node_id: 'intake' },
      { ts: '2026-05-27T14:42:04Z', event: 'trigger fired', node_id: 'trigger' },
    ],
    logs: [
      { ts: '2026-05-27T14:42:11Z', level: 'info', node_id: 'notify', message: 'op notified' },
      { ts: '2026-05-27T14:42:09Z', level: 'info', node_id: 'persist', message: 'row inserted id=4881' },
      { ts: '2026-05-27T14:42:07Z', level: 'info', node_id: 'ai_extract', message: 'confidence=0.94' },
      { ts: '2026-05-27T14:41:50Z', level: 'warn', node_id: 'mediant_lookup', message: 'latency 410ms (above p95)' },
      { ts: '2026-05-27T14:40:12Z', level: 'error', node_id: 'job_match', message: 'no candidate above threshold (corr=cor_a3)' },
    ],
    metrics: {
      throughput_per_min: [6, 7, 8, 5, 9, 11, 8, 7, 10, 12, 9, 8],
      latency_p50_ms: [6800, 7100, 6900, 7300, 7400, 7100, 6700, 7200, 7300, 7000, 7200, 7300],
      latency_p95_ms: [11200, 12000, 11800, 12400, 12700, 12100, 11500, 12300, 12200, 12000, 12100, 12400],
      failure_count_24h: 15,
      success_rate_pct: 96.4,
      api_errors_24h: 3,
      token_usage_24h: 184300,
      infra_cost_24h_usd: 7.42,
      human_review_rate: 0.027,
    },
  },

  runs: seedRuns('wf-receiving-match', 'RUN-RM', 22, 0.96),

  versions: [
    { version: '3.2.1', deployed_at: '2026-05-18T19:03:22Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'platform-lead@northpoint.com', status: 'active', migration_notes: 'Raise confidence threshold from 0.75 → 0.80 after Q1 evaluation.', changes: ['Threshold 0.75 → 0.80', 'Add retry on Mediant timeout', 'Token budget cap per run'] },
    { version: '3.1.0', deployed_at: '2026-04-02T13:18:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'platform-lead@northpoint.com', status: 'superseded', migration_notes: 'Swap to claude-sonnet-4-6 vision.', changes: ['Model: claude-sonnet-4 → claude-sonnet-4-6', 'New prompt template'] },
    { version: '3.0.0', deployed_at: '2026-01-21T09:00:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'platform-lead@northpoint.com', status: 'superseded', migration_notes: 'Initial vision-based matcher rollout.', changes: ['Replace rule-based matcher with AI extract', 'Add No Job Found routing'] },
  ],

  security: {
    rbac_roles: [
      { role: 'fulfillment_operator', permissions: ['workflow.read', 'run.replay', 'queue.triage'], member_count: 14 },
      { role: 'fulfillment_lead', permissions: ['workflow.*', 'run.*', 'version.deploy'], member_count: 2 },
      { role: 'platform_admin', permissions: ['*'], member_count: 4 },
    ],
    secrets: [
      { name: 'MEDIANT_API_KEY', masked_value: 'med_••••••••••a7c2', rotation_interval_days: 90, last_rotated: '2026-04-12T00:00:00Z' },
      { name: 'ANTHROPIC_API_KEY', masked_value: 'sk-ant-••••••••8f12', rotation_interval_days: 60, last_rotated: '2026-05-02T00:00:00Z' },
    ],
    pii_classification: [
      { field: 'client_name', classification: 'PII — masked in logs' },
      { field: 'account_number', classification: 'PII — never logged' },
    ],
  },

  audit: [
    { id: 'A-994', ts: '2026-05-18T19:03:22Z', actor: 'derrick@nexusfrontier.tech', action: 'version.deploy', target: 'v3.2.1', details: 'Promoted to prod after staging soak.' },
    { id: 'A-988', ts: '2026-05-17T21:11:00Z', actor: 'derrick@nexusfrontier.tech', action: 'prompt.edit', target: 'ai_extract', details: 'Tightened doc-type extraction wording.' },
    { id: 'A-982', ts: '2026-05-12T15:00:00Z', actor: 'platform-lead@northpoint.com', action: 'approval', target: 'v3.2.1', details: 'Technical approval for threshold change.' },
    { id: 'A-979', ts: '2026-05-10T10:24:00Z', actor: 'ops-oncall@northpoint.com', action: 'manual_intervention', target: 'RUN-RM-9874', details: 'Force-matched after operator review.' },
  ],
};

// =========================================================================
// 2) No Job Found Handling
// =========================================================================
const wfNoJobFound: Workflow = {
  id: 'wf-no-job-found',
  name: 'No Job Found Handling',
  description: 'Triages material that did not match a job — operator review + automated re-match.',
  category: 'Fulfillment',
  owner: 'Fulfillment Ops',
  environment: 'prod',
  status: 'degraded',
  criticality: 'tier-2',
  version: '1.4.0',
  executions_24h: 18,
  success_rate: 88.9,
  avg_duration_ms: 14200,
  integrations_count: 2,
  last_run_at: '2026-05-27T13:58:00Z',
  last_deployed_at: '2026-05-10T17:00:00Z',
  ai_enabled: true,
  tags: ['fulfillment', 'exception', 'human-in-the-loop'],
  sla_status: 'at-risk',
  active_runs: 1,

  business_summary:
    'When the matcher cannot confidently link a material to a job, this workflow surfaces the item to a fulfillment operator with AI-suggested next steps and retries matching after operator input.',
  kpis: {
    executions_per_day: 18,
    failure_rate: 11.1,
    avg_duration_ms: 14200,
    p95_duration_ms: 38000,
    retry_rate: 0.4,
    queue_depth: 3,
    human_interventions: 18,
    ai_confidence_avg: 0.74,
    token_usage_24h: 24100,
    est_cost_per_run_usd: 0.022,
  },
  dependency_summary: 'Upstream: Material Receiving (low-confidence branch). Downstream: matched_job_materials, archive bin.',
  recent_incidents: [
    { id: 'INC-2141', title: 'Queue backlog > 25', severity: 'high', status: 'investigating', opened_at: '2026-05-26T18:30:00Z', summary: 'Backlog above SLA threshold; operator capacity reduced overnight.' },
  ],

  identity: {
    workflow_id: 'wf-no-job-found',
    namespace: 'fulfillment',
    slug: 'no-job-found-handling',
    created_by: 'derrick@nexusfrontier.tech',
    created_at: '2025-12-10T10:00:00Z',
    updated_at: '2026-05-10T17:00:00Z',
    git_commit: 'f2cd22a',
    deployment_environment: 'prod',
    lifecycle_state: 'degraded',
  },
  governance: {
    owner_team: 'Fulfillment Ops',
    escalation_contacts: ['ops-oncall@northpoint.com'],
    business_approver: 'VP Operations',
    technical_approver: 'Director, Platform Eng',
    compliance_scope: ['Internal Ops SLA'],
    data_classification: 'Internal',
    risk_level: 'medium',
  },
  policy: {
    retention_policy: 'Queue items retained 30 days after resolution.',
    residency_policy: 'US-East only.',
    secrets_used: ['ANTHROPIC_API_KEY'],
    approval_requirements: 'Single approver.',
    data_access_scope: 'fulfillment namespace.',
  },

  connections: [
    { id: 'c-jobs', name: 'Ace Jobs DB', category: 'database', type: 'PostgreSQL', authentication_type: 'IAM', owner: 'Platform Eng', permissions_scope: 'read/write', environment: 'prod', latency_ms: 12, uptime_pct: 99.99, last_success: '2026-05-27T13:58:00Z', rate_limits: '2k rps' },
    { id: 'c-anthropic', name: 'Anthropic Claude API', category: 'ai-provider', type: 'Chat', authentication_type: 'API key', owner: 'Platform Eng', permissions_scope: 'claude-sonnet-4-6', environment: 'prod', latency_ms: 1740, uptime_pct: 99.91, last_success: '2026-05-27T13:57:00Z', rate_limits: '50 rpm', secrets_reference: 'ANTHROPIC_API_KEY' },
    { id: 'c-humanq', name: 'Operator Review Queue', category: 'human-queue', type: 'In-app queue', authentication_type: 'JWT', owner: 'Fulfillment Ops', permissions_scope: 'read/write queue', environment: 'prod', latency_ms: 8, uptime_pct: 100, last_success: '2026-05-27T13:58:00Z', rate_limits: 'n/a' },
  ],

  graph: {
    nodes: [
      { id: 'trigger', type: 'event', label: 'NoMatch Event', description: 'Receives no-match events from receiving.', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 10, p95_latency_ms: 25, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ extraction }', output: '{ queue_item_id }' }, recent_logs: ['event consumed'] },
      { id: 'enqueue', type: 'queue', label: 'Enqueue Review', description: 'Adds the item to the operator queue.', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 60, p95_latency_ms: 120, success_rate: 99.99, retries_24h: 0 }, io_schema: { input: '{ extraction }', output: '{ queue_item_id }' }, recent_logs: ['queued id=qi_482'] },
      { id: 'suggest', type: 'ai', label: 'AI Suggest Next', description: 'Ace suggests likely matching jobs based on partial extraction.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 1700, p95_latency_ms: 3200, success_rate: 98.2, retries_24h: 1 }, io_schema: { input: '{ extraction }', output: '{ suggestions[] }' }, recent_logs: ['3 suggestions returned'] },
      { id: 'operator', type: 'human-approval', label: 'Operator Review', description: 'Operator confirms a match or marks unresolvable.', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 9000, p95_latency_ms: 32000, success_rate: 96, retries_24h: 0 }, io_schema: { input: '{ suggestions[] }', output: '{ decision }' }, recent_logs: ['decision=match job_id=j_8821'] },
      { id: 'rematch', type: 'system', label: 'Rematch & Persist', description: 'Re-runs match using operator-supplied job and persists.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 220, p95_latency_ms: 400, success_rate: 99.5, retries_24h: 1 }, io_schema: { input: '{ decision }', output: '{ row_id }' }, recent_logs: ['row inserted'] },
      { id: 'archive', type: 'system', label: 'Archive Unresolvable', description: 'Sends unresolvable items to the archive bin.', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 80, p95_latency_ms: 140, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ queue_item_id }', output: '{}' }, recent_logs: ['archived qi_481'] },
    ],
    edges: [
      { id: 'e1', source: 'trigger', target: 'enqueue' },
      { id: 'e2', source: 'enqueue', target: 'suggest' },
      { id: 'e3', source: 'suggest', target: 'operator' },
      { id: 'e4', source: 'operator', target: 'rematch', label: 'match' },
      { id: 'e5', source: 'operator', target: 'archive', label: 'unresolvable' },
    ],
  },

  logic: {
    executive_summary: 'Get unmatched materials to a confirmed disposition (match or archive) within the operator SLA.',
    trigger_conditions: ['no-match event emitted by Material Receiving workflow'],
    sop_steps: [
      { title: '1. Enqueue', detail: 'Add item to operator queue with extraction context.' },
      { title: '2. AI suggest', detail: 'Generate top-3 candidate jobs with rationale.' },
      { title: '3. Operator review', detail: 'Operator picks a match or marks unresolvable.' },
      { title: '4. Rematch / Archive', detail: 'Persist match or move to archive.' },
    ],
    decision_logic: ['Operator picks match → rematch + persist', 'Operator marks unresolvable → archive', 'No operator action within 4h → escalate'],
    exception_handling: ['Suggest API failure → operator reviews without suggestions', 'Persist failure → re-queue'],
    escalation_rules: ['Queue depth > 25 → page fulfillment-lead', 'Item age > 4h → flag urgent'],
    ai_reasoning: {
      model: 'claude-sonnet-4-6',
      system_prompt_excerpt: ACE_PROMPT_EXCERPT,
      confidence_threshold: 0.6,
      grounding_sources: ['intake_log', 'mediant.public.jobs'],
      fallback_logic: 'Ace falls back to scripted mock if ANTHROPIC_API_KEY missing — operator reviews without AI suggestions.',
      safety_rules: ['Mask client info in suggestions panel preview'],
      tool_permissions: ['db.read[ops_jobs]', 'db.read[mediant]'],
      evaluation_metrics: ['operator_acceptance_rate', 'time_to_disposition'],
    },
  },

  observability: {
    timeline: [
      { ts: '2026-05-27T13:58:00Z', event: 'operator confirmed match', node_id: 'operator' },
      { ts: '2026-05-27T13:50:00Z', event: 'suggestions generated', node_id: 'suggest' },
    ],
    logs: [
      { ts: '2026-05-27T13:58:00Z', level: 'info', node_id: 'rematch', message: 'persisted row 4882' },
      { ts: '2026-05-26T18:30:00Z', level: 'warn', node_id: 'enqueue', message: 'queue depth=27 (above SLA)' },
    ],
    metrics: {
      throughput_per_min: [0, 0, 1, 0, 1, 1, 0, 0, 1, 1, 1, 0],
      latency_p50_ms: [12000, 11800, 14000, 16000, 14200, 13900, 12200, 14400, 14000, 13800, 14100, 14200],
      latency_p95_ms: [32000, 34000, 38000, 41000, 38000, 37000, 33000, 39000, 38000, 36000, 37000, 38000],
      failure_count_24h: 2,
      success_rate_pct: 88.9,
      api_errors_24h: 1,
      token_usage_24h: 24100,
      infra_cost_24h_usd: 0.92,
      human_review_rate: 1.0,
    },
  },
  runs: seedRuns('wf-no-job-found', 'RUN-NJF', 18, 0.89),
  versions: [
    { version: '1.4.0', deployed_at: '2026-05-10T17:00:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'platform-lead@northpoint.com', status: 'active', migration_notes: 'Add AI suggestions panel.', changes: ['Add suggest node', 'Lower threshold for suggestions to 0.50'] },
    { version: '1.3.0', deployed_at: '2026-03-18T11:00:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'platform-lead@northpoint.com', status: 'superseded', migration_notes: 'Add archive path.', changes: ['Add archive node'] },
    { version: '1.2.0', deployed_at: '2026-02-02T15:00:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'platform-lead@northpoint.com', status: 'superseded', migration_notes: 'Operator queue v2.', changes: ['Redesign operator queue UI'] },
  ],
  security: {
    rbac_roles: [
      { role: 'fulfillment_operator', permissions: ['queue.triage', 'run.replay'], member_count: 14 },
      { role: 'fulfillment_lead', permissions: ['queue.*', 'workflow.read'], member_count: 2 },
    ],
    secrets: [{ name: 'ANTHROPIC_API_KEY', masked_value: 'sk-ant-••••••••8f12', rotation_interval_days: 60, last_rotated: '2026-05-02T00:00:00Z' }],
    pii_classification: [{ field: 'client_name', classification: 'PII — masked in queue UI' }],
  },
  audit: [
    { id: 'A-996', ts: '2026-05-10T17:00:00Z', actor: 'derrick@nexusfrontier.tech', action: 'version.deploy', target: 'v1.4.0', details: 'Added AI suggestions.' },
    { id: 'A-990', ts: '2026-05-08T09:00:00Z', actor: 'fulfillment-lead@northpoint.com', action: 'approval', target: 'v1.4.0', details: 'Business approval.' },
  ],
};

// =========================================================================
// 3) Material Shortage / Received Short
// =========================================================================
const wfReceivedShort: Workflow = {
  id: 'wf-received-short',
  name: 'Material Shortage / Received Short',
  description: 'Reconciles short or wrong-material shipments and triggers re-orders.',
  category: 'Fulfillment',
  owner: 'Fulfillment Ops',
  environment: 'prod',
  status: 'healthy',
  criticality: 'tier-2',
  version: '2.0.3',
  executions_24h: 7,
  success_rate: 100.0,
  avg_duration_ms: 5200,
  integrations_count: 2,
  last_run_at: '2026-05-27T11:21:00Z',
  last_deployed_at: '2026-04-29T14:00:00Z',
  ai_enabled: true,
  tags: ['fulfillment', 'exception', 'reorder'],
  sla_status: 'on-track',
  active_runs: 0,

  business_summary:
    'When a receipt arrives short or with the wrong document type, this workflow logs the discrepancy, notifies the distributor, and triggers a corrective re-order via the warehouse system.',
  kpis: { executions_per_day: 7, failure_rate: 0, avg_duration_ms: 5200, p95_duration_ms: 9100, retry_rate: 0.1, queue_depth: 1, human_interventions: 1, ai_confidence_avg: 0.88, token_usage_24h: 8400, est_cost_per_run_usd: 0.014 },
  dependency_summary: 'Upstream: Material Receiving (received-short branch). Downstream: distributor notification, re-order queue.',
  recent_incidents: [],

  identity: { workflow_id: 'wf-received-short', namespace: 'fulfillment', slug: 'received-short', created_by: 'derrick@nexusfrontier.tech', created_at: '2025-12-15T10:00:00Z', updated_at: '2026-04-29T14:00:00Z', git_commit: '4241937', deployment_environment: 'prod', lifecycle_state: 'active' },
  governance: { owner_team: 'Fulfillment Ops', escalation_contacts: ['ops-oncall@northpoint.com'], business_approver: 'VP Operations', technical_approver: 'Director, Platform Eng', compliance_scope: ['Internal Ops SLA'], data_classification: 'Internal', risk_level: 'low' },
  policy: { retention_policy: '24 months.', residency_policy: 'US-East only.', secrets_used: ['DISTRIBUTOR_WEBHOOK_KEY'], approval_requirements: 'Single approver.', data_access_scope: 'fulfillment namespace + distributors table.' },

  connections: [
    { id: 'c-jobs', name: 'Ace Jobs DB', category: 'database', type: 'PostgreSQL', authentication_type: 'IAM', owner: 'Platform Eng', permissions_scope: 'read/write', environment: 'prod', latency_ms: 11, uptime_pct: 99.99, last_success: '2026-05-27T11:21:00Z', rate_limits: '2k rps' },
    { id: 'c-distributor', name: 'Distributor Webhook', category: 'api', type: 'HTTPS webhook', authentication_type: 'HMAC', owner: 'Fulfillment Ops', permissions_scope: 'notify', environment: 'prod', latency_ms: 320, uptime_pct: 99.4, last_success: '2026-05-27T11:21:00Z', rate_limits: '60 rpm', secrets_reference: 'DISTRIBUTOR_WEBHOOK_KEY' },
  ],

  graph: {
    nodes: [
      { id: 'trigger', type: 'event', label: 'Received Short Event', description: 'Receives short/wrong-doc events.', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 12, p95_latency_ms: 30, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ receipt }', output: '{ case_id }' }, recent_logs: ['event consumed'] },
      { id: 'classify', type: 'ai', label: 'Classify Discrepancy', description: 'AI classifies as short, wrong type, or damaged.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 1100, p95_latency_ms: 2000, success_rate: 98.5, retries_24h: 0 }, io_schema: { input: '{ receipt }', output: '{ class }' }, recent_logs: ['class=short'] },
      { id: 'notify_dist', type: 'notification', label: 'Notify Distributor', description: 'Calls distributor webhook with case details.', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 320, p95_latency_ms: 700, success_rate: 99.4, retries_24h: 1 }, io_schema: { input: '{ case_id }', output: '{ ack }' }, recent_logs: ['ack=ok'] },
      { id: 'reorder', type: 'integration', label: 'Trigger Re-order', description: 'Pushes a re-order to the warehouse system.', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 410, p95_latency_ms: 900, success_rate: 99.8, retries_24h: 0 }, io_schema: { input: '{ case_id }', output: '{ reorder_id }' }, recent_logs: ['reorder ro_3321 created'] },
      { id: 'close', type: 'system', label: 'Close Case', description: 'Marks case closed in the jobs DB.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 60, p95_latency_ms: 120, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ case_id }', output: '{}' }, recent_logs: ['case closed'] },
    ],
    edges: [
      { id: 'e1', source: 'trigger', target: 'classify' },
      { id: 'e2', source: 'classify', target: 'notify_dist' },
      { id: 'e3', source: 'notify_dist', target: 'reorder' },
      { id: 'e4', source: 'reorder', target: 'close' },
    ],
  },

  logic: {
    executive_summary: 'Reconcile shipment discrepancies and trigger corrective re-orders without operator effort in 95% of cases.',
    trigger_conditions: ['received-short event from Material Receiving'],
    sop_steps: [
      { title: '1. Classify', detail: 'AI labels the discrepancy class.' },
      { title: '2. Notify', detail: 'Webhook the distributor with case details.' },
      { title: '3. Re-order', detail: 'Push a corrective re-order.' },
      { title: '4. Close', detail: 'Close the case.' },
    ],
    decision_logic: ['Short → quantity-only re-order', 'Wrong type → swap re-order + return label', 'Damaged → escalate to operator'],
    exception_handling: ['Webhook 5xx → retry 3× with backoff'],
    escalation_rules: ['Damaged class → operator review'],
    ai_reasoning: {
      model: 'claude-sonnet-4-6',
      system_prompt_excerpt: ACE_PROMPT_EXCERPT,
      confidence_threshold: 0.7,
      grounding_sources: ['receipts', 'distributors'],
      fallback_logic: 'Scripted classifier fallback if AI unavailable.',
      safety_rules: ['Do not include client names in webhook payload'],
      tool_permissions: ['db.read[receipts]'],
      evaluation_metrics: ['classification_accuracy', 'reorder_lead_time'],
    },
  },

  observability: {
    timeline: [{ ts: '2026-05-27T11:21:00Z', event: 'case closed', node_id: 'close' }],
    logs: [{ ts: '2026-05-27T11:21:00Z', level: 'info', node_id: 'close', message: 'case wc_881 closed' }],
    metrics: { throughput_per_min: [0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0], latency_p50_ms: [4800, 5100, 5000, 5200, 5300, 5200, 4900, 5200, 5400, 5100, 5200, 5200], latency_p95_ms: [8400, 8900, 8700, 9100, 9300, 9200, 8500, 9000, 9400, 8800, 9100, 9100], failure_count_24h: 0, success_rate_pct: 100, api_errors_24h: 0, token_usage_24h: 8400, infra_cost_24h_usd: 0.31, human_review_rate: 0.14 },
  },
  runs: seedRuns('wf-received-short', 'RUN-WS', 12, 1.0),
  versions: [
    { version: '2.0.3', deployed_at: '2026-04-29T14:00:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'platform-lead@northpoint.com', status: 'active', migration_notes: 'Webhook retry tuning.', changes: ['Webhook retries 1 → 3', 'Backoff multiplier 1.5'] },
    { version: '2.0.0', deployed_at: '2026-02-14T09:00:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'platform-lead@northpoint.com', status: 'superseded', migration_notes: 'AI classifier rollout.', changes: ['Replace rule-based classifier with AI'] },
    { version: '1.5.0', deployed_at: '2025-12-15T10:00:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'platform-lead@northpoint.com', status: 'superseded', migration_notes: 'Initial release.', changes: ['Initial implementation'] },
  ],
  security: {
    rbac_roles: [
      { role: 'fulfillment_operator', permissions: ['workflow.read', 'run.replay'], member_count: 14 },
      { role: 'fulfillment_lead', permissions: ['workflow.*', 'version.deploy'], member_count: 2 },
    ],
    secrets: [{ name: 'DISTRIBUTOR_WEBHOOK_KEY', masked_value: 'dw_••••••••1f04', rotation_interval_days: 90, last_rotated: '2026-04-01T00:00:00Z' }],
    pii_classification: [{ field: 'client_name', classification: 'PII — never logged' }],
  },
  audit: [
    { id: 'A-998', ts: '2026-04-29T14:00:00Z', actor: 'derrick@nexusfrontier.tech', action: 'version.deploy', target: 'v2.0.3', details: 'Retry tuning rollout.' },
  ],
};

// =========================================================================
// 4) Corporate Actions Tender Offer
// =========================================================================
const wfCaTenderOffer: Workflow = {
  id: 'wf-ca-tender-offer',
  name: 'Corporate Actions Tender Offer',
  description: 'Processes incoming advisor emails about tender offers — eligibility, tax, position lookup, election support.',
  category: 'Corporate Actions',
  owner: 'CA Operations',
  environment: 'prod',
  status: 'healthy',
  criticality: 'tier-1',
  version: '1.1.0',
  executions_24h: 38,
  success_rate: 94.7,
  avg_duration_ms: 18400,
  integrations_count: 6,
  last_run_at: '2026-05-27T14:30:00Z',
  last_deployed_at: '2026-05-25T16:30:00Z',
  ai_enabled: true,
  tags: ['corporate-actions', 'email', 'mcp', 'tender-offer'],
  sla_status: 'on-track',
  active_runs: 2,

  business_summary:
    'When an advisor emails about a tender offer, Ace ingests the message, identifies the issuer + event, pulls affected client positions, evaluates eligibility, computes tax implications, and drafts a response. Operator approves before Ace sends.',
  kpis: { executions_per_day: 38, failure_rate: 5.3, avg_duration_ms: 18400, p95_duration_ms: 32000, retry_rate: 2.1, queue_depth: 2, human_interventions: 38, ai_confidence_avg: 0.88, token_usage_24h: 412000, est_cost_per_run_usd: 0.084 },
  dependency_summary: 'Upstream: Outlook MCP (advisor mailbox). Downstream: Outlook MCP (send), Money Movement Pack (elections).',
  recent_incidents: [{ id: 'INC-2147', title: 'Security Master cache miss spike', severity: 'low', status: 'resolved', opened_at: '2026-05-26T08:15:00Z', summary: 'Cold start after deploy. Auto-recovered.' }],

  identity: { workflow_id: 'wf-ca-tender-offer', namespace: 'corporate-actions', slug: 'tender-offer', created_by: 'derrick@nexusfrontier.tech', created_at: '2026-04-10T10:00:00Z', updated_at: '2026-05-25T16:30:00Z', git_commit: '3dce121', deployment_environment: 'prod', lifecycle_state: 'active' },
  governance: { owner_team: 'CA Operations', escalation_contacts: ['ca-oncall@northpoint.com', 'ca-lead@northpoint.com'], business_approver: 'Head of CA', technical_approver: 'Director, Platform Eng', compliance_scope: ['SOC 2', 'FINRA', 'Internal CA SLA'], data_classification: 'Confidential', risk_level: 'high' },
  policy: { retention_policy: 'All run records + responses retained 7 years (regulatory).', residency_policy: 'US-East only; no cross-border processing.', secrets_used: ['OUTLOOK_MCP_TOKEN', 'SECMASTER_API_KEY', 'DATAXCHANGE_KEY', 'MONEYMOVE_KEY', 'TAXLOT_KEY', 'ANTHROPIC_API_KEY'], approval_requirements: 'Dual approval for prompt/model changes; single for non-AI changes.', data_access_scope: 'CA namespace + client positions scoped by advisor.' },

  connections: [
    { id: 'c-outlook', name: 'Outlook MCP', category: 'mcp', type: 'MCP (Outlook)', authentication_type: 'OAuth2', owner: 'CA Operations', permissions_scope: 'read advisor inbox; send as ops@', environment: 'prod', latency_ms: 380, uptime_pct: 99.92, last_success: '2026-05-27T14:30:00Z', rate_limits: '500 rpm', secrets_reference: 'OUTLOOK_MCP_TOKEN' },
    { id: 'c-ca-domain', name: 'CA Domain Pack', category: 'mcp', type: 'MCP (CA events)', authentication_type: 'API key', owner: 'CA Operations', permissions_scope: 'read events, deadlines', environment: 'prod', latency_ms: 120, uptime_pct: 99.98, last_success: '2026-05-27T14:29:50Z', rate_limits: '1k rpm' },
    { id: 'c-secmaster', name: 'Security Master', category: 'mcp', type: 'MCP (security ref data)', authentication_type: 'mTLS', owner: 'Platform Eng', permissions_scope: 'read tickers, CUSIPs', environment: 'prod', latency_ms: 90, uptime_pct: 99.99, last_success: '2026-05-27T14:29:55Z', rate_limits: '2k rpm', secrets_reference: 'SECMASTER_API_KEY' },
    { id: 'c-dataxchange', name: 'Data Xchange', category: 'mcp', type: 'MCP (positions)', authentication_type: 'OAuth2', owner: 'Platform Eng', permissions_scope: 'read positions scoped to advisor', environment: 'prod', latency_ms: 220, uptime_pct: 99.95, last_success: '2026-05-27T14:30:00Z', rate_limits: '300 rpm', secrets_reference: 'DATAXCHANGE_KEY' },
    { id: 'c-moneymove', name: 'Money Movement Pack', category: 'mcp', type: 'MCP (elections)', authentication_type: 'OAuth2', owner: 'CA Operations', permissions_scope: 'submit elections', environment: 'prod', latency_ms: 410, uptime_pct: 99.9, last_success: '2026-05-27T13:55:00Z', rate_limits: '100 rpm', secrets_reference: 'MONEYMOVE_KEY' },
    { id: 'c-taxlot', name: 'Tax Lot Pack', category: 'mcp', type: 'MCP (tax lots)', authentication_type: 'OAuth2', owner: 'Platform Eng', permissions_scope: 'read tax lots', environment: 'prod', latency_ms: 280, uptime_pct: 99.94, last_success: '2026-05-27T14:29:30Z', rate_limits: '200 rpm', secrets_reference: 'TAXLOT_KEY' },
    { id: 'c-anthropic', name: 'Anthropic Claude API', category: 'ai-provider', type: 'Chat', authentication_type: 'API key', owner: 'Platform Eng', permissions_scope: 'claude-sonnet-4-6', environment: 'prod', latency_ms: 1900, uptime_pct: 99.91, last_success: '2026-05-27T14:29:58Z', rate_limits: '50 rpm tier', secrets_reference: 'ANTHROPIC_API_KEY' },
  ],

  graph: {
    nodes: [
      { id: 'trigger', type: 'trigger', label: 'Email Intake (Outlook)', description: 'Fires on new advisor email tagged ops@.', owner: 'CA Operations', status: 'idle', metrics: { avg_latency_ms: 380, p95_latency_ms: 700, success_rate: 99.92, retries_24h: 0 }, io_schema: { input: '{ message }', output: '{ email_id }' }, recent_logs: ['email_id=email-001'] },
      { id: 'extract', type: 'ai', label: 'AI Extract (issuer + event)', description: 'Ace parses subject + body to identify issuer, event type, ticker.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 2200, p95_latency_ms: 3400, success_rate: 98.5, retries_24h: 3 }, io_schema: { input: '{ message }', output: '{ issuer, event, ticker }' }, recent_logs: ['issuer=Acme event=tender'] },
      { id: 'ca_event', type: 'integration', label: 'Resolve CA Event', description: 'Pull event details from CA Domain Pack.', owner: 'CA Operations', status: 'idle', metrics: { avg_latency_ms: 130, p95_latency_ms: 220, success_rate: 99.98, retries_24h: 0 }, io_schema: { input: '{ ticker, event }', output: '{ event_record }' }, recent_logs: ['event resolved'] },
      { id: 'secmaster', type: 'integration', label: 'Security Master Lookup', description: 'Resolve CUSIP / ticker normalization.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 90, p95_latency_ms: 200, success_rate: 99.99, retries_24h: 0 }, io_schema: { input: '{ ticker }', output: '{ cusip }' }, recent_logs: ['cusip resolved'] },
      { id: 'positions', type: 'integration', label: 'Pull Positions (Data Xchange)', description: 'Scoped client holdings by advisor.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 240, p95_latency_ms: 480, success_rate: 99.7, retries_24h: 1 }, io_schema: { input: '{ cusip, advisor_id }', output: '{ positions[] }' }, recent_logs: ['18 positions'] },
      { id: 'taxlots', type: 'integration', label: 'Tax Lot Pack', description: 'Lot-level cost basis per position.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 290, p95_latency_ms: 540, success_rate: 99.6, retries_24h: 2 }, io_schema: { input: '{ positions[] }', output: '{ lots[] }' }, recent_logs: ['lots fetched'] },
      { id: 'eligibility', type: 'transform', label: 'Eligibility & Tax', description: 'Compute eligible accounts + tax implications.', owner: 'CA Operations', status: 'idle', metrics: { avg_latency_ms: 140, p95_latency_ms: 240, success_rate: 99.9, retries_24h: 0 }, io_schema: { input: '{ event, positions[], lots[] }', output: '{ eligibility }' }, recent_logs: ['12 eligible accounts'] },
      { id: 'draft', type: 'ai', label: 'AI Draft Response', description: 'Ace drafts the response email grounded in the analysis.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 2800, p95_latency_ms: 4400, success_rate: 99.1, retries_24h: 1 }, io_schema: { input: '{ eligibility }', output: '{ draft }' }, recent_logs: ['draft generated'] },
      { id: 'operator', type: 'human-approval', label: 'Operator Approval', description: 'CA operator approves or edits the draft.', owner: 'CA Operations', status: 'idle', metrics: { avg_latency_ms: 9000, p95_latency_ms: 22000, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ draft }', output: '{ approved }' }, recent_logs: ['approved by jsmith'] },
      { id: 'send', type: 'integration', label: 'Send via Outlook MCP', description: 'Sends the approved email.', owner: 'CA Operations', status: 'idle', metrics: { avg_latency_ms: 410, p95_latency_ms: 800, success_rate: 99.92, retries_24h: 0 }, io_schema: { input: '{ approved }', output: '{ message_id }' }, recent_logs: ['sent message-id=msg_4421'] },
    ],
    edges: [
      { id: 'e1', source: 'trigger', target: 'extract' },
      { id: 'e2', source: 'extract', target: 'ca_event' },
      { id: 'e3', source: 'ca_event', target: 'secmaster' },
      { id: 'e4', source: 'secmaster', target: 'positions' },
      { id: 'e5', source: 'positions', target: 'taxlots' },
      { id: 'e6', source: 'taxlots', target: 'eligibility' },
      { id: 'e7', source: 'eligibility', target: 'draft' },
      { id: 'e8', source: 'draft', target: 'operator' },
      { id: 'e9', source: 'operator', target: 'send' },
    ],
  },

  logic: {
    executive_summary: 'Turn a single advisor email into an accurate, eligibility-aware response within the advisor SLA — with operator-in-the-loop approval before send.',
    trigger_conditions: ['New email to ops@ inbox routed via Outlook MCP', 'Subject or body references a tender/merger/election event'],
    sop_steps: [
      { title: '1. Extract', detail: 'Ace identifies issuer, event type, ticker from subject + body.' },
      { title: '2. Resolve event', detail: 'CA Domain Pack returns event record including deadlines.' },
      { title: '3. Resolve security', detail: 'Security Master normalizes ticker → CUSIP.' },
      { title: '4. Pull positions', detail: 'Data Xchange returns client positions scoped to advisor.' },
      { title: '5. Lots + tax', detail: 'Tax Lot Pack returns cost basis per lot.' },
      { title: '6. Eligibility', detail: 'Compute eligible accounts and tax implications.' },
      { title: '7. Draft', detail: 'Ace drafts the response email grounded in the analysis.' },
      { title: '8. Approve & send', detail: 'Operator approves or edits → Outlook MCP sends.' },
    ],
    decision_logic: [
      'Event deadline < 24h → flag as urgent in draft',
      'Tax basis disposition: FIFO if not specified by advisor',
      'Accounts with restricted lots → exclude from eligible list with explanation',
    ],
    exception_handling: [
      'Outlook MCP send failure → retry 3× with backoff; surface failure to operator',
      'Data Xchange empty positions → draft acknowledges "no holdings" rather than fabricate',
      'Tax Lot Pack timeout → draft uses position-level basis with disclaimer',
    ],
    escalation_rules: [
      'Confidence on extraction < 0.70 → escalate to CA lead before draft',
      'Multi-event email → split into separate runs',
    ],
    ai_reasoning: {
      model: 'claude-sonnet-4-6',
      system_prompt_excerpt: ACE_PROMPT_EXCERPT,
      confidence_threshold: 0.7,
      grounding_sources: ['Outlook message body', 'CA Domain Pack event record', 'Security Master', 'Data Xchange positions', 'Tax Lot Pack lots'],
      fallback_logic: 'If ANTHROPIC_API_KEY missing, manager_agent.py returns scripted mock; workflow halts at Draft and routes to operator with raw analysis.',
      safety_rules: [
        'Never draft a send without operator approval',
        'Never include client account numbers in email body',
        'Cite source MCPs for every figure quoted',
      ],
      tool_permissions: ['mcp.outlook.read', 'mcp.outlook.send (gated)', 'mcp.ca_domain.read', 'mcp.secmaster.read', 'mcp.dataxchange.read', 'mcp.taxlot.read'],
      evaluation_metrics: ['eligibility_accuracy', 'operator_edit_rate', 'time_to_send'],
    },
  },

  observability: {
    timeline: [
      { ts: '2026-05-27T14:30:00Z', event: 'sent', node_id: 'send' },
      { ts: '2026-05-27T14:29:50Z', event: 'operator approved', node_id: 'operator' },
      { ts: '2026-05-27T14:29:30Z', event: 'draft generated', node_id: 'draft' },
      { ts: '2026-05-27T14:29:10Z', event: '12 eligible accounts', node_id: 'eligibility' },
      { ts: '2026-05-27T14:28:50Z', event: 'positions pulled (18)', node_id: 'positions' },
    ],
    logs: [
      { ts: '2026-05-27T14:30:00Z', level: 'info', node_id: 'send', message: 'msg_4421 sent' },
      { ts: '2026-05-27T14:29:30Z', level: 'info', node_id: 'draft', message: 'tokens=3140 confidence=0.91' },
      { ts: '2026-05-26T08:15:00Z', level: 'warn', node_id: 'secmaster', message: 'cache miss; cold-start fetched' },
    ],
    metrics: { throughput_per_min: [1, 2, 1, 1, 2, 3, 2, 1, 2, 2, 1, 2], latency_p50_ms: [17000, 18200, 17800, 18400, 18900, 18100, 17400, 18300, 18600, 18000, 18200, 18400], latency_p95_ms: [30000, 31000, 30500, 32000, 33000, 31500, 29000, 31500, 32500, 30500, 31000, 32000], failure_count_24h: 2, success_rate_pct: 94.7, api_errors_24h: 4, token_usage_24h: 412000, infra_cost_24h_usd: 18.40, human_review_rate: 1.0 },
  },
  runs: seedRuns('wf-ca-tender-offer', 'RUN-TO', 24, 0.95),

  versions: [
    { version: '1.1.0', deployed_at: '2026-05-25T16:30:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'ca-lead@northpoint.com', status: 'active', migration_notes: 'Add Tax Lot Pack lookup before draft.', changes: ['Add taxlots node', 'Update draft prompt to cite lot-level basis'] },
    { version: '1.0.1', deployed_at: '2026-05-12T12:00:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'ca-lead@northpoint.com', status: 'superseded', migration_notes: 'Retry tuning on Outlook send.', changes: ['Send retries 1 → 3'] },
    { version: '1.0.0', deployed_at: '2026-04-10T10:00:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'ca-lead@northpoint.com', status: 'superseded', migration_notes: 'Initial 5-step CA tender offer rollout.', changes: ['Initial implementation'] },
  ],

  security: {
    rbac_roles: [
      { role: 'ca_operator', permissions: ['workflow.read', 'run.replay', 'draft.approve'], member_count: 6 },
      { role: 'ca_lead', permissions: ['workflow.*', 'run.*', 'version.deploy'], member_count: 1 },
      { role: 'platform_admin', permissions: ['*'], member_count: 4 },
    ],
    secrets: [
      { name: 'OUTLOOK_MCP_TOKEN', masked_value: 'ms_••••••••2c91', rotation_interval_days: 30, last_rotated: '2026-05-05T00:00:00Z' },
      { name: 'SECMASTER_API_KEY', masked_value: 'sm_••••••••d401', rotation_interval_days: 90, last_rotated: '2026-04-12T00:00:00Z' },
      { name: 'DATAXCHANGE_KEY', masked_value: 'dx_••••••••6f88', rotation_interval_days: 60, last_rotated: '2026-04-28T00:00:00Z' },
      { name: 'MONEYMOVE_KEY', masked_value: 'mm_••••••••a112', rotation_interval_days: 60, last_rotated: '2026-04-30T00:00:00Z' },
      { name: 'TAXLOT_KEY', masked_value: 'tl_••••••••9b34', rotation_interval_days: 60, last_rotated: '2026-04-30T00:00:00Z' },
      { name: 'ANTHROPIC_API_KEY', masked_value: 'sk-ant-••••••••8f12', rotation_interval_days: 60, last_rotated: '2026-05-02T00:00:00Z' },
    ],
    pii_classification: [
      { field: 'client_name', classification: 'PII — masked in logs, present in draft only' },
      { field: 'account_number', classification: 'PII — never in email body or logs' },
      { field: 'tax_basis', classification: 'Confidential — summarized only' },
    ],
  },

  audit: [
    { id: 'A-1010', ts: '2026-05-25T16:30:00Z', actor: 'derrick@nexusfrontier.tech', action: 'version.deploy', target: 'v1.1.0', details: 'Added Tax Lot Pack integration.' },
    { id: 'A-1008', ts: '2026-05-24T11:00:00Z', actor: 'ca-lead@northpoint.com', action: 'approval', target: 'v1.1.0', details: 'Business + technical approval.' },
    { id: 'A-1004', ts: '2026-05-22T09:00:00Z', actor: 'ca-operator-1@northpoint.com', action: 'draft.approve', target: 'RUN-TO-10218', details: 'Edited draft to clarify deadline.' },
    { id: 'A-1001', ts: '2026-05-20T14:00:00Z', actor: 'derrick@nexusfrontier.tech', action: 'prompt.edit', target: 'draft', details: 'Tightened citation requirements.' },
  ],
};

// =========================================================================
// 5) Overs (excess stock)
// =========================================================================
const wfOvers: Workflow = {
  id: 'wf-overs',
  name: 'Overs Handling',
  description: 'Detects excess stock on completed jobs and routes it for storage confirmation.',
  category: 'Fulfillment',
  owner: 'Fulfillment Ops',
  environment: 'prod',
  status: 'healthy',
  criticality: 'tier-3',
  version: '1.0.0',
  executions_24h: 3,
  success_rate: 100.0,
  avg_duration_ms: 2100,
  integrations_count: 1,
  last_run_at: '2026-06-15T15:40:00Z',
  last_deployed_at: '2026-06-16T09:00:00Z',
  ai_enabled: true,
  tags: ['fulfillment', 'inventory', 'overs'],
  sla_status: 'on-track',
  active_runs: 0,

  business_summary:
    'When a job is marked Complete and more material was received than ordered, this workflow flags the excess as Overs and notifies the intake team to confirm the amount and storage location, after which it is logged to the Overs inventory.',
  kpis: { executions_per_day: 3, failure_rate: 0, avg_duration_ms: 2100, p95_duration_ms: 3400, retry_rate: 0, queue_depth: 1, human_interventions: 1, ai_confidence_avg: 0.95, token_usage_24h: 2600, est_cost_per_run_usd: 0.004 },
  dependency_summary: 'Upstream: Material Receiving (job marked Complete with excess). Downstream: Overs queue, Inventory ▸ Overs.',
  recent_incidents: [],

  identity: { workflow_id: 'wf-overs', namespace: 'fulfillment', slug: 'overs', created_by: 'derrick@nexusfrontier.tech', created_at: '2026-06-16T09:00:00Z', updated_at: '2026-06-16T09:00:00Z', git_commit: '4241937', deployment_environment: 'prod', lifecycle_state: 'active' },
  governance: { owner_team: 'Fulfillment Ops', escalation_contacts: ['ops-oncall@northpoint.com'], business_approver: 'VP Operations', technical_approver: 'Director, Platform Eng', compliance_scope: ['Internal Ops SLA'], data_classification: 'Internal', risk_level: 'low' },
  policy: { retention_policy: '24 months.', residency_policy: 'US-East only.', secrets_used: [], approval_requirements: 'Single approver.', data_access_scope: 'fulfillment namespace + receipts table.' },

  connections: [
    { id: 'c-jobs', name: 'Ace Jobs DB', category: 'database', type: 'PostgreSQL', authentication_type: 'IAM', owner: 'Platform Eng', permissions_scope: 'read/write', environment: 'prod', latency_ms: 11, uptime_pct: 99.99, last_success: '2026-06-15T15:40:00Z', rate_limits: '2k rps' },
  ],

  graph: {
    nodes: [
      { id: 'trigger', type: 'event', label: 'Job Completed', description: 'Fires when a receipt\'s job is marked Complete.', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 10, p95_latency_ms: 25, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ receipt }', output: '{ receipt }' }, recent_logs: ['job complete'] },
      { id: 'check_excess', type: 'ai', label: 'Verify Excess', description: 'Compares received vs ordered per document type.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 900, p95_latency_ms: 1600, success_rate: 99.5, retries_24h: 0 }, io_schema: { input: '{ receipt }', output: '{ overage }' }, recent_logs: ['overage detected'] },
      { id: 'flag_overs', type: 'system', label: 'Flag Overs (Pending)', description: 'Sets overs_status=Pending and posts a review note to the receipt thread.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 120, p95_latency_ms: 240, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ overage }', output: '{ overs_id }' }, recent_logs: ['overs flagged'] },
      { id: 'notify', type: 'notification', label: 'Notify Intake Team', description: 'Surfaces the item on the Overs queue for review.', owner: 'Fulfillment Ops', status: 'idle', metrics: { avg_latency_ms: 80, p95_latency_ms: 160, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ overs_id }', output: '{ ack }' }, recent_logs: ['queued'] },
    ],
    edges: [
      { id: 'e1', source: 'trigger', target: 'check_excess' },
      { id: 'e2', source: 'check_excess', target: 'flag_overs' },
      { id: 'e3', source: 'flag_overs', target: 'notify' },
    ],
  },

  logic: {
    executive_summary: 'Catch excess stock on completed jobs and route it for storage confirmation, keeping inventory accurate.',
    trigger_conditions: ['job_status set to Complete with received > ordered'],
    sop_steps: [
      { title: '1. Detect', detail: 'Compare received vs ordered per document type on job completion.' },
      { title: '2. Flag', detail: 'Mark the receipt overs_status = Pending.' },
      { title: '3. Notify', detail: 'Surface on the Overs queue + receipt chat thread.' },
      { title: '4. Confirm', detail: 'Operator confirms amount + storage location → Completed.' },
    ],
    decision_logic: ['received > ordered → flag Overs', 'received is the same as or less than ordered → no action'],
    exception_handling: ['Missing ordered quantities → skip (no overage computable)'],
    escalation_rules: ['Unconfirmed > 7 days → remind intake lead'],
    ai_reasoning: {
      model: 'claude-sonnet-4-6',
      system_prompt_excerpt: ACE_PROMPT_EXCERPT,
      confidence_threshold: 0.7,
      grounding_sources: ['receipts', 'job_materials'],
      fallback_logic: 'Deterministic received-vs-ordered comparison if AI unavailable.',
      safety_rules: ['Do not auto-confirm storage — always require operator review'],
      tool_permissions: ['db.read[receipts]'],
      evaluation_metrics: ['overage_detection_accuracy', 'confirmation_lead_time'],
    },
  },

  observability: {
    timeline: [{ ts: '2026-06-15T15:40:00Z', event: 'overs flagged', node_id: 'flag_overs' }],
    logs: [{ ts: '2026-06-15T15:40:00Z', level: 'info', node_id: 'flag_overs', message: 'overs flagged for S-P42' }],
    metrics: { throughput_per_min: [0, 0, 1, 0, 0, 0, 1, 0, 0, 1, 0, 0], latency_p50_ms: [2000, 2100, 2050, 2100, 2200, 2100, 2000, 2100, 2300, 2100, 2100, 2100], latency_p95_ms: [3200, 3400, 3300, 3400, 3500, 3400, 3200, 3400, 3600, 3400, 3400, 3400], failure_count_24h: 0, success_rate_pct: 100, api_errors_24h: 0, token_usage_24h: 2600, infra_cost_24h_usd: 0.09, human_review_rate: 1.0 },
  },
  runs: seedRuns('wf-overs', 'RUN-OV', 6, 1.0),
  versions: [
    { version: '1.0.0', deployed_at: '2026-06-16T09:00:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'platform-lead@northpoint.com', status: 'active', migration_notes: 'Initial release.', changes: ['Initial implementation'] },
  ],
  security: {
    rbac_roles: [
      { role: 'fulfillment_operator', permissions: ['workflow.read', 'run.replay'], member_count: 14 },
      { role: 'fulfillment_lead', permissions: ['workflow.*', 'version.deploy'], member_count: 2 },
    ],
    secrets: [],
    pii_classification: [{ field: 'client_name', classification: 'PII — never logged' }],
  },
  audit: [
    { id: 'A-1100', ts: '2026-06-16T09:00:00Z', actor: 'derrick@nexusfrontier.tech', action: 'version.deploy', target: 'v1.0.0', details: 'Initial Overs workflow release.' },
  ],
};

// =========================================================================
// 6) Material Shortage Follow-Up (ICT-13)
// =========================================================================
const wfShortageFollowup: Workflow = {
  id: 'wf-shortage-followup',
  name: 'Material Shortage Follow-Up',
  description: 'Proxy/ICT-owned follow-up on Received Short receipts — drafts and sends the Material Shortage Notice, logs the MIC follow-up, and handles solicitor responses (ICT-13).',
  category: 'Fulfillment',
  owner: 'Proxy/ICT',
  environment: 'prod',
  status: 'healthy',
  criticality: 'tier-2',
  version: '1.0.0',
  executions_24h: 6,
  success_rate: 97.0,
  avg_duration_ms: 9200,
  integrations_count: 4,
  last_run_at: '2026-06-16T16:10:00Z',
  last_deployed_at: '2026-06-17T09:00:00Z',
  ai_enabled: true,
  tags: ['fulfillment', 'collections', 'shortage', 'email', 'ict-13'],
  sla_status: 'on-track',
  active_runs: 1,

  business_summary:
    'When a receipt is dispositioned Received Short and escalated to Proxy/ICT, this workflow runs the manual ICT-13 follow-up end-to-end: it pulls the shorted line-items from the jobs DB, looks up the CUSIP online, resolves the solicitor from the Broker Search Card, drafts the Material Shortage Notice, sends it (operator-in-the-loop), logs a "Material Short" follow-up in MIC, and records the solicitor response — asking the solicitor to approve printing charges (no-cost POD within the 100pp/50-unit guideline is an internal ICT call; outside it, management approves). Outreach repeats every 4 days until the material is received.',
  kpis: { executions_per_day: 6, failure_rate: 3.0, avg_duration_ms: 9200, p95_duration_ms: 16000, retry_rate: 0.3, queue_depth: 2, human_interventions: 6, ai_confidence_avg: 0.93, token_usage_24h: 9600, est_cost_per_run_usd: 0.016 },
  dependency_summary: 'Upstream: Received Short disposition (escalated to Proxy/ICT). Downstream: Outlook (solicitor email), MIC follow-up log, documents@/verification@ notifications.',
  recent_incidents: [],

  identity: { workflow_id: 'wf-shortage-followup', namespace: 'collections', slug: 'material-shortage-followup', created_by: 'derrick@nexusfrontier.tech', created_at: '2026-06-17T09:00:00Z', updated_at: '2026-06-17T09:00:00Z', git_commit: '97cefcf', deployment_environment: 'prod', lifecycle_state: 'active' },
  governance: { owner_team: 'Proxy/ICT', escalation_contacts: ['cary-lead@northpoint.com', 'ops-oncall@northpoint.com'], business_approver: 'Manager, Collections', technical_approver: 'Director, Platform Eng', compliance_scope: ['Internal Ops SLA', 'ICT-13'], data_classification: 'Internal', risk_level: 'low' },
  policy: { retention_policy: '24 months.', residency_policy: 'US-East only.', secrets_used: ['OUTLOOK_MCP_TOKEN', 'OPENFIGI_API_KEY'], approval_requirements: 'Operator sends directly; POD outside guidelines requires management approval.', data_access_scope: 'collections namespace + receipts/job_materials.' },

  connections: [
    { id: 'c-jobs', name: 'Ace Jobs DB', category: 'database', type: 'PostgreSQL', authentication_type: 'IAM', owner: 'Platform Eng', permissions_scope: 'read: job_materials (ordered vs received)', environment: 'prod', latency_ms: 11, uptime_pct: 99.99, last_success: '2026-06-16T16:10:00Z', rate_limits: '2k rps' },
    { id: 'c-cusip', name: 'CUSIP Lookup (OpenFIGI / EDGAR)', category: 'api', type: 'HTTPS (online lookup)', authentication_type: 'API key', owner: 'Proxy/ICT', permissions_scope: 'read: issuer → CUSIP', environment: 'prod', latency_ms: 410, uptime_pct: 99.5, last_success: '2026-06-16T16:09:50Z', rate_limits: '25 rpm', secrets_reference: 'OPENFIGI_API_KEY' },
    { id: 'c-brokercard', name: 'Broker Search Card', category: 'api', type: 'External system (mocked)', authentication_type: 'SSO', owner: 'Mediant', permissions_scope: 'read: solicitor contact by job', environment: 'prod', latency_ms: 520, uptime_pct: 99.2, last_success: '2026-06-16T16:09:55Z', rate_limits: 'n/a' },
    { id: 'c-outlook', name: 'Outlook MCP', category: 'mcp', type: 'MCP (Outlook)', authentication_type: 'OAuth2', owner: 'Proxy/ICT', permissions_scope: 'send shortage notice; notify documents@/verification@', environment: 'prod', latency_ms: 380, uptime_pct: 99.92, last_success: '2026-06-16T16:10:00Z', rate_limits: '500 rpm', secrets_reference: 'OUTLOOK_MCP_TOKEN' },
  ],

  graph: {
    nodes: [
      { id: 'trigger', type: 'trigger', label: 'Received Short Escalation', description: 'Fires when a receipt is dispositioned Received Short and escalated to Proxy/ICT.', owner: 'Proxy/ICT', status: 'idle', metrics: { avg_latency_ms: 12, p95_latency_ms: 30, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ receipt }', output: '{ receipt_id }' }, recent_logs: ['escalation received'] },
      { id: 'load_shortage', type: 'database', label: 'Load Shortage (Ace DB)', description: 'Pulls shorted line-items (ordered vs received) from the jobs DB. SMRF/VIF excluded per policy.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 60, p95_latency_ms: 140, success_rate: 99.99, retries_24h: 0 }, io_schema: { input: '{ receipt_id }', output: '{ jobNumber, lineItems[] }' }, recent_logs: ['3 line-items, 1 skipped (SMRF)'] },
      { id: 'lookup_cusip', type: 'api', label: 'Lookup CUSIP (online)', description: 'Maps issuer name → CUSIP via OpenFIGI / SEC EDGAR.', owner: 'Proxy/ICT', status: 'idle', metrics: { avg_latency_ms: 410, p95_latency_ms: 900, success_rate: 99.0, retries_24h: 1 }, io_schema: { input: '{ issuerName }', output: '{ cusip }' }, recent_logs: ['cusip resolved'] },
      { id: 'resolve_solicitor', type: 'api', label: 'Resolve Solicitor (Broker Card)', description: 'Resolves the solicitor contact from the Broker Search Card on the job.', owner: 'Mediant', status: 'idle', metrics: { avg_latency_ms: 520, p95_latency_ms: 1100, success_rate: 98.5, retries_24h: 1 }, io_schema: { input: '{ jobNumber }', output: '{ name, email }' }, recent_logs: ['solicitor resolved'] },
      { id: 'draft_notice', type: 'transform', label: 'Draft Material Shortage Notice', description: 'Renders the ICT-13 Notice table (job, issuer, CUSIP, material, ordered, received, shortage).', owner: 'Proxy/ICT', status: 'idle', metrics: { avg_latency_ms: 120, p95_latency_ms: 260, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ jobNumber, cusip, lineItems[] }', output: '{ email }' }, recent_logs: ['notice drafted'] },
      { id: 'send', type: 'human-approval', label: 'Send to Solicitor', description: 'Operator reviews and sends the notice directly (no separate approver).', owner: 'Proxy/ICT', status: 'idle', metrics: { avg_latency_ms: 8000, p95_latency_ms: 20000, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ email }', output: '{ sent }' }, recent_logs: ['sent to solicitor'] },
      { id: 'log_followup', type: 'system', label: 'Log MIC Follow-Up', description: 'Writes a "Material Short" follow-up note (qty + solicitor) to the receipt log.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 80, p95_latency_ms: 160, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ sent }', output: '{ note_id }' }, recent_logs: ['follow-up logged'] },
      { id: 'await_response', type: 'conditional', label: 'Await Solicitor Response', description: 'Branches on the solicitor reply: not-needed, POD, or will-ship.', owner: 'Proxy/ICT', status: 'idle', metrics: { avg_latency_ms: 5, p95_latency_ms: 10, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ response }', output: '{ branch }' }, recent_logs: ['branch=pod'] },
      { id: 'notify_docs', type: 'notification', label: 'Notify documents@/verification@', description: 'Notifies on not-needed or POD outcomes.', owner: 'Proxy/ICT', status: 'idle', metrics: { avg_latency_ms: 320, p95_latency_ms: 700, success_rate: 99.9, retries_24h: 0 }, io_schema: { input: '{ outcome }', output: '{ ack }' }, recent_logs: ['notified'] },
      { id: 'pod_decision', type: 'conditional', label: 'POD Decision', description: 'Solicitor is asked to pay for POD first; 100 pages or less and 50 shorted or less → no-cost POD is an internal ICT call; otherwise escalate to management.', owner: 'Proxy/ICT', status: 'idle', metrics: { avg_latency_ms: 6, p95_latency_ms: 12, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ pages, shorted }', output: '{ eligible }' }, recent_logs: ['escalate (124pp)'] },
      { id: 'reschedule', type: 'system', label: 'Schedule Re-Outreach', description: 'Schedules the next outreach 4 days out until the material is received.', owner: 'Proxy/ICT', status: 'idle', metrics: { avg_latency_ms: 40, p95_latency_ms: 90, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ jobNumber }', output: '{ next_run }' }, recent_logs: ['next outreach tomorrow'] },
      { id: 'close', type: 'system', label: 'Close Follow-Up', description: 'Marks the shortage follow-up resolved.', owner: 'Platform Eng', status: 'idle', metrics: { avg_latency_ms: 50, p95_latency_ms: 110, success_rate: 100, retries_24h: 0 }, io_schema: { input: '{ outcome }', output: '{}' }, recent_logs: ['follow-up closed'] },
    ],
    edges: [
      { id: 'e1', source: 'trigger', target: 'load_shortage' },
      { id: 'e2', source: 'load_shortage', target: 'lookup_cusip' },
      { id: 'e3', source: 'lookup_cusip', target: 'resolve_solicitor' },
      { id: 'e4', source: 'resolve_solicitor', target: 'draft_notice' },
      { id: 'e5', source: 'draft_notice', target: 'send' },
      { id: 'e6', source: 'send', target: 'log_followup' },
      { id: 'e7', source: 'log_followup', target: 'await_response' },
      { id: 'e8', source: 'await_response', target: 'notify_docs', label: 'not needed' },
      { id: 'e9', source: 'await_response', target: 'pod_decision', label: 'POD' },
      { id: 'e10', source: 'await_response', target: 'reschedule', label: 'will ship' },
      { id: 'e11', source: 'pod_decision', target: 'notify_docs' },
      { id: 'e12', source: 'notify_docs', target: 'close' },
      { id: 'e13', source: 'reschedule', target: 'close' },
    ],
  },

  logic: {
    executive_summary: 'Compress the manual, multi-system ICT-13 shortage follow-up into one human-in-the-loop agent flow: gather → draft → send → log → handle response, chasing every 4 days until the material arrives.',
    trigger_conditions: ['Receipt dispositioned Received Short and escalated to Proxy/ICT'],
    sop_steps: [
      { title: '1. Load shortage', detail: 'Pull shorted line-items (ordered vs received) from the jobs DB; skip SMRF/VIF.' },
      { title: '2. Lookup CUSIP', detail: 'Map issuer → CUSIP via online lookup (OpenFIGI / EDGAR).' },
      { title: '3. Resolve solicitor', detail: 'Resolve the solicitor from the Broker Search Card on the job.' },
      { title: '4. Draft notice', detail: 'Render the Material Shortage Notice table.' },
      { title: '5. Send + log', detail: 'Operator sends; log a "Material Short" follow-up in MIC; schedule the next follow-up in 4 days.' },
      { title: '6. Handle response', detail: 'Record not-needed / POD / will-ship; notify documents@/verification@; apply POD rule.' },
    ],
    decision_logic: [
      'Material type is SMRF or VIF → skip follow-up (ICT-13 section 6.i)',
      'POD requested → ask the solicitor to approve printing charges; at 100 pages or less and 50 shorted or less, no-cost POD is an internal ICT call',
      'POD requested & outside guideline → escalate to management',
      'No response → re-outreach every 4 days until material received',
    ],
    exception_handling: ['CUSIP lookup miss → operator enters manually', 'Outlook send failure → retry 3× with backoff'],
    escalation_rules: ['POD outside 100pp/50-unit guideline → management approval', 'No response after repeated outreach → Proxy/ICT lead'],
    ai_reasoning: {
      model: 'claude-sonnet-4-6',
      system_prompt_excerpt: ACE_PROMPT_EXCERPT,
      confidence_threshold: 0.7,
      grounding_sources: ['job_materials', 'receipts', 'Broker Search Card', 'CUSIP lookup'],
      fallback_logic: 'Notice is a deterministic template; if AI polish is unavailable the rendered table is sent as-is.',
      safety_rules: ['Operator sends directly — no automated send without review', 'Do not POD outside guidelines without management approval'],
      tool_permissions: ['db.read[job_materials]', 'http.get[cusip]', 'http.get[broker_card]', 'mcp.outlook.send (gated)'],
      evaluation_metrics: ['time_to_first_outreach', 'response_resolution_rate', 'pod_escalation_rate'],
    },
  },

  observability: {
    timeline: [{ ts: '2026-06-16T16:10:00Z', event: 'follow-up logged', node_id: 'log_followup' }],
    logs: [{ ts: '2026-06-16T16:10:00Z', level: 'info', node_id: 'send', message: 'notice sent for job 2975320' }],
    metrics: { throughput_per_min: [0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0], latency_p50_ms: [8800, 9100, 9000, 9200, 9400, 9200, 8900, 9200, 9500, 9100, 9200, 9200], latency_p95_ms: [15000, 15800, 15400, 16000, 16400, 16100, 15000, 16000, 16500, 15500, 16000, 16000], failure_count_24h: 0, success_rate_pct: 97.0, api_errors_24h: 1, token_usage_24h: 9600, infra_cost_24h_usd: 0.34, human_review_rate: 1.0 },
  },
  runs: seedRuns('wf-shortage-followup', 'RUN-SF', 9, 0.97),
  versions: [
    { version: '1.0.0', deployed_at: '2026-06-17T09:00:00Z', deployed_by: 'derrick@nexusfrontier.tech', approver: 'cary-lead@northpoint.com', status: 'active', migration_notes: 'Initial ICT-13 shortage follow-up release.', changes: ['Initial implementation'] },
  ],
  security: {
    rbac_roles: [
      { role: 'collections_operator', permissions: ['workflow.read', 'run.replay', 'shortage.send'], member_count: 8 },
      { role: 'collections_lead', permissions: ['workflow.*', 'version.deploy', 'pod.approve'], member_count: 2 },
    ],
    secrets: [
      { name: 'OUTLOOK_MCP_TOKEN', masked_value: 'ms_••••••••2c91', rotation_interval_days: 30, last_rotated: '2026-05-05T00:00:00Z' },
      { name: 'OPENFIGI_API_KEY', masked_value: 'figi_••••••••4d70', rotation_interval_days: 180, last_rotated: '2026-04-01T00:00:00Z' },
    ],
    pii_classification: [{ field: 'solicitor_email', classification: 'Business contact — not PII' }],
  },
  audit: [
    { id: 'A-1200', ts: '2026-06-17T09:00:00Z', actor: 'derrick@nexusfrontier.tech', action: 'version.deploy', target: 'v1.0.0', details: 'Initial Material Shortage Follow-Up (ICT-13) release.' },
  ],
};

export const WORKFLOWS: Workflow[] = [
  wfReceivingMatch,
  wfNoJobFound,
  wfReceivedShort,
  wfOvers,
  wfCaTenderOffer,
  wfShortageFollowup,
];
